import { HttpBackend, HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { retry, tap } from 'rxjs/operators';

// eslint-disable-next-line @nx/nx/enforce-module-boundaries
import { ConfigService as AbstractConfigService } from '@stylehaven/components';
import { Observable, of } from 'rxjs';
import { DigihavenEnvironmentConfig } from '../../entities/config/environment-config.entity';

@Injectable()
export class ConfigService<T extends DigihavenEnvironmentConfig> extends AbstractConfigService<T> {
    protected readonly FALLBACK_VALUE: T = {} as T;
    protected readonly TYPE_NAME: string;

    private configData!: T;

    private readonly http: HttpClient;

    constructor(private handler: HttpBackend) {
        super();
        this.http = new HttpClient(handler);
    }

    load(path: string): Promise<T | undefined> {
        return this.http
            .get<T>(path)
            .pipe(
                retry(2),
                tap(result => (this.configData = result))
            )
            .toPromise();
    }

    get config(): T {
        return this.configData;
    }

    public initialize(): Observable<T> {
        return of({} as T);
    }

    // This function maps Zeticon's internal quarterly release number to DAV's release version which has 2 per year.
    getDisplayApplicationVersion(): string {
        const version = this.config?.applicationVersion;
        if (!version) {
            return '';
        }

        // Expected inputs like:
        // - "26.2-develop.2182"
        // - "26.1.1"
        const [rawMajor, rawMinor] = version.split('.');

        const major = Number.parseInt(rawMajor ?? '', 10);
        const minor = Number.parseInt((rawMinor ?? '').match(/^\d+/)?.[0] ?? '', 10);

        if (Number.isNaN(major) || Number.isNaN(minor)) {
            return '';
        }

        let year = 2000 + major;
        let releaseIndex = 1;
        if (minor === 2 || minor === 3) {
            // The releases in quarter 2 and 3 are rolled into one internal DAV release
            releaseIndex = 2;
        } else if (minor === 4) {
            // The release in quarter 4 is rolled into the next years first release
            year += 1;
        }

        return `${year}-${releaseIndex}`;
    }
}
